import { useEffect, useState } from "react";
import useLocalStorageState from "use-local-storage-state";
import ChatBubble from "../ChatBubble/ChatBubble";
import ChatWindow from "../ChatWindow/ChatWindow";
import ChatWidgetCommandHandler from "../ChatWidgetCommandHandler";
import { MitelLivechat } from "../MitelLivechat";
import { GiosgLivechat } from "../GiosgLivechat";
import { CHAT_REQUEST, CHAT_BOT_REQUEST, CHAT_RESPONSE, CLOSE_CHAT, START_LIVECHAT_REQUESTED } from "@/common/events/chatWidgetEvents";
import type {
  ChatConfig,
  ChatMessage,
  ChatRequestPayload,
} from "../../types/chat.types";
import styles from "./ChatWidget.module.css";
import { postChatMessageService } from "@/common/services/postChatMessageService";
import { endChatSessionService } from "@/common/services/endChatSessionService";
import { ConfigRequestService } from "@/common/services/configService";
import { domEventService } from "@/common/services/domEventService";
import { useChatContext } from "@/common/context/ChatContext";
import { useAccessToken, useTokenData, useEntraToken } from "@/common/context/AuthContext";
import CloseChatModal from "../CloseChatModal/CloseChatModal";
import { urlPolling } from "@/common/services/urlPolling";
import { getSessionIdCookie, removeSessionIdCookie, setSessionIdCookie } from "@/common/utils";
import { flushData } from "@/common/services/appInsightsService";
import {
  CHAT_HISTORY_KEY,
  CHAT_HISTORY_BEFORE_LOGIN_KEY,
  CHAT_LOGIN_HANDLING_KEY,
  CHAT_OPEN_STATE_KEY,
  CHAT_OPEN_BEFORE_LOGIN_KEY,
  CHAT_CONTEXT_KEY,
  EXPIRED_SESSION_MESSAGE
} from "@/common/utils/constants";


const defaultConfig: ChatConfig = {
  context: "",
  botDisplayName: "Hjälprobot",
  botSenderName: "Hjälprobot",
  introMessageText: "Hej",
  introMessageLinkUrl: "",
  introMessageLinkText: "",
  introMessageLinkAriaLabel: "",
  greeting: {
    userUrl: "",
    messages: [],
  },
  launchIconImageURL: "",
  closeChatImageURL: "",
  minimizeChatImageURL: "",
  calloutMessage: "",
  sendMessageImageURL: "",
  uploadImageURL: "",
  botAvatarImageURL: "",
  agentAvatarImageURL: "",
  visibleBotAvatar: false,
  visibleLaunchIcon: false,
  enabledCallout: false,
  additionalData: {}, // Sent to the backend with each message via the 'context' request field
  livechatProvider: "none",
  shouldShowChatBubble: () => true,
  autoOpen: false,
  shouldShowCloseModal: true,
  openLinksInNewTab: false,
};

interface ChatWidgetProps {
  config: Partial<ChatConfig>;
  startLogin?: () => Promise<any>;
  getOpcoContextData?: () => object;
  autoInputToTriage?: string;
  isLivechatAvailable?: () => boolean;
  lazyInit?: boolean;
  refreshToken?: () => Promise<string>;
}

const ChatWidget = ({
  config,
  startLogin,
  getOpcoContextData,
  autoInputToTriage,
  isLivechatAvailable,
  lazyInit = true,
  refreshToken,
}: ChatWidgetProps) => {
  const isStorageCleared = localStorage.getItem(CHAT_HISTORY_KEY) === null;

  const { chatSessionStarted, setChatSessionStarted, internalEventEmitter } = useChatContext();
  const chatBubbleButton = document.getElementById("chat-bubble-button");
  const [isOpen, setIsOpen] = useLocalStorageState(CHAT_OPEN_STATE_KEY, {
    defaultValue: false,
  });
  const [hasEverOpened, setHasEverOpened] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const [showCloseChatModal, setShowCloseChatModal] = useState(false);
  const [autoInputToTriageFromUrl, setAutoInputToTriageFromUrl] = useState(autoInputToTriage || "");
  const [hasAutoTriageSent, setHasAutoTriageSent] = useState(false);
  const [context, setContext] = useLocalStorageState(CHAT_CONTEXT_KEY, {
    defaultValue: "",
  });
  const [mergedConfig, setMergedConfig] = useState<ChatConfig>({
    ...defaultConfig,
    ...config,
  });
  const [messages, setMessages] = useLocalStorageState<ChatMessage[]>(
    CHAT_HISTORY_KEY,
    {
      defaultValue: [],
    },
  );

  const LOGIN_DEBOUNCE_MS = 500;

  const getAccessToken = useAccessToken();
  const getTokenData = useTokenData();
  const getEntraToken = useEntraToken();
  const userUrl = urlPolling(!!mergedConfig.visibleLaunchIcon, 1000);

  useEffect(() => {
    const mergeConfig = () => {
      if (config) {
        setMergedConfig({ ...defaultConfig, ...config });
      }
    };

    mergeConfig();
  }, [config]);

  useEffect(() => {
    if (mergedConfig.visibleLaunchIcon && mergedConfig.shouldShowChatBubble(userUrl)) {
      setShowBubble(true);
    }
  }, []);

  const handleOpenChat = (context?: string) => {
    setIsOpen(true);
    setHasEverOpened(true);
    if (context !== undefined) {
      setContext(context);
    }
    domEventService.emitEvent('chatbotMaximized', { context });
  };

  const handleSetContextCommand = (context: string) => {
    setContext(context);
  };

  useEffect(() => {
    domEventService.emitEvent("chatbotInitialized");
  }, []);

  useEffect(() => {
    if (mergedConfig.autoOpen && !isOpen) {
      console.log('[ChatWidget] Auto-opening chat widget');
      handleOpenChat();
    }
  }, [mergedConfig.autoOpen]);

  // Clear backend session if user-side storage was [possibly] cleared by site
  useEffect(() => {
    // Skip clearing session if using lazy init and chat hasn't been opened yet
    if (lazyInit && !hasEverOpened) {
      return;
    }

    if (isStorageCleared) {
      console.log("Clearing backend session");
      const clearSession = async () => {
        try {
          onEndSession();
        } catch (error) {
          console.error("Failed to clear backend session:", error);
        }
      };
      clearSession();
    }
  }, [hasEverOpened]);

  useEffect(() => {
    // Skip fetching greeting if using lazy init and chat hasn't been opened yet
    if (lazyInit && !hasEverOpened) {
      return;
    }

    const fetchGreeting = async () => {
      const accessToken = await getAccessToken();
      const requestBody = {
        userUrl: btoa(userUrl),
        accessToken,
      };

      try {
        const data = await ConfigRequestService(requestBody, refreshToken);
        setMergedConfig((prevConfig) => ({
          ...prevConfig,
          greeting: {
            userUrl: data.greeting?.userUrl || userUrl,
            messages: data.greeting?.messages || [
              "Hej! Hur kan jag hjälpa dig?",
            ],
          },
        }));
      } catch (error) {
        console.error("Failed to fetch config:", error);
      }
    };

    fetchGreeting();
  }, [hasEverOpened]);

  const handleAuthenticationOnOpen = async (): Promise<boolean> => {
    if (!isOpen || !mergedConfig.triggerLoginOnChatOpen || !startLogin || messages.length !== 0) {
      return true;
    }

    console.debug('[ChatWidget] Chat opened - triggering login');
    try {
      await startLogin();
      return true;
    } catch (error: any) {
      console.warn('[ChatWidget] Failed to trigger login on chat open:', error);
      
      if (error?.message === 'LOGIN_RETRY_NEEDED') {
        setMessages([
          {
            sender: "system",
            content: "För att kunna chatta behöver du logga in. Tillåt popup-fönster i din webbläsare och skriv sedan ett meddelande här för att fortsätta.",
            type: "system",
          },
        ]);
      }
      return false;
    }
  };

  useEffect(() => {
    const handleAutoRouteToAgentOnInit = async () => {
      const authSucceeded = await handleAuthenticationOnOpen();
      if (!authSucceeded) {
        return;
      }
      
      if (isOpen && autoInputToTriageFromUrl && messages.length === 0 && !hasAutoTriageSent) {
        sendMessage("user", autoInputToTriageFromUrl, { autoPost: true });
        setHasAutoTriageSent(true);
      }

      if (isOpen && context && messages.length === 0) {
        if (context === "trigger_repayment_blanco") {
          sendMessage("user", "jag vill lösa mitt privatlån", {
            autoPost: true,
          });
        } else if (context === "trigger_amortization_blanco") {
          sendMessage("user", "jag vill extraamortera på mitt privatlån", {
            autoPost: true,
          });
        } else if (context === "book_meeting") {
          sendMessage("user", "jag vill boka ett möte", {
            autoPost: true,
          });
        }
        setContext("");
      }
    };
    handleAutoRouteToAgentOnInit();
  }, [isOpen, context]);

  useEffect(() => {
    if (userUrl && mergedConfig.visibleLaunchIcon) {
      setShowBubble(mergedConfig.shouldShowChatBubble(userUrl));
    }
  }, [userUrl]);

  const handleMinimizeChat = () => {
    setIsOpen(false);
    if (mergedConfig.visibleLaunchIcon && mergedConfig.shouldShowChatBubble(userUrl)) {
      setShowBubble(true);
    }
    domEventService.emitEvent('chatbotMinimized');

    if (chatBubbleButton) {
      chatBubbleButton.focus();
    }

    setShowCloseChatModal(false);
  };

  const handleCloseChat = async () => {
    domEventService.emitEvent("chatbotCloseButtonClicked");

    internalEventEmitter.emit(CLOSE_CHAT, {});
    onEndSession();

    if (chatBubbleButton) {
      chatBubbleButton.focus();
    }

    setMessages([]);
    setIsOpen(false);
    if (mergedConfig.visibleLaunchIcon && mergedConfig.shouldShowChatBubble(userUrl)) {
      setShowBubble(true);
    }

    setChatSessionStarted(false);
    setHasAutoTriageSent(false);
  };

  const handleStartLivechat = () => {
    console.log('[ChatWidget] handleStartLivechat');
    internalEventEmitter.emit(START_LIVECHAT_REQUESTED, {});
  };

  const handleSendMessage = (
    sender: string,
    content: string,
    options?: { autoPost?: boolean },
  ) => {
    const payload: ChatRequestPayload = { sender, content, options, handled: false };
    internalEventEmitter.emit(CHAT_REQUEST, payload);
    
    if (!payload.handled) {
      sendMessage(sender, content, options);
    }
  };

  const onEndSession = async () => {
    const accessToken = await getAccessToken();
    await endChatSessionService({
      accessToken,
    });
    removeSessionIdCookie();
    flushData();
  };

  /**
   * Strips characters that might trigger OWASP alerts on request.
   */
  const stripUnsafeCharacters = (text: string): string => {
    return text.replace(/[\[\]()]/g, '_');
  };

  const sendMessage = async (
    sender: string,
    content: string,
    options?: { autoPost?: boolean; baseMessages?: ChatMessage[] },
  ) => {
    const sessionId = getSessionIdCookie();
    const accessToken = await getAccessToken();
    const entraToken = await getEntraToken();
    
    const tempMessages = [...(options?.baseMessages || messages || [])];

    tempMessages.push({
      sender,
      content,
      type: "user",
      ...(options?.autoPost && { autoPost: true }),
    });

    setMessages(tempMessages);
    setIsTyping(true);
    
    const opcoContextData = getOpcoContextData ? getOpcoContextData() : {};
    const requestContext = {
      context: context,
      ...(sessionId && { sessionId }),
      ...mergedConfig.additionalData,
      ...{ accessToken },
      ...(entraToken && { entraToken }),
      ...opcoContextData,
    };

    const requestBody = {
      messages: tempMessages.map((m) => ({
        content: stripUnsafeCharacters(m.content),
        role: m.type === "user" ? "user" : "assistant",
      })),
      context: requestContext,
      url: btoa(userUrl),
      sessionId: sessionId
    };
    
    const requestPayload = { requestBody };
    internalEventEmitter.emit(CHAT_BOT_REQUEST, requestPayload);

    try {
      const response = await postChatMessageService(requestBody, refreshToken);
      const data = await response.json();

      const responsePayload = { data };
      internalEventEmitter.emit(CHAT_RESPONSE, responsePayload);

      if (response.headers.get("X-New-Session")) {
        const isNotFirstSession = messages.filter((msg) => msg.type === "user").length > 1;
        if (isNotFirstSession) {
          setMessages((prev) => [
            ...(prev || []),
            {
              sender: "system",
              content: EXPIRED_SESSION_MESSAGE,
              type: "system",
            },
          ]);
        }
      }

      if (data?.sessionId) {
        setSessionIdCookie(data.sessionId, 30);
      }

      if (
        startLogin &&
        typeof startLogin === "function" &&
        data?.triggerLogin
      ) {
        const hasQuickReplies = data.teneoWebClient?.quick_replies && data.teneoWebClient.quick_replies.length > 0;
        const defaultContent = hasQuickReplies ? "" : "Jag har tyvärr inget svar just nu.";
        
        const messagesBeforeLogin = [
          ...tempMessages,
          {
            sender: "Bot",
            content: data.content || defaultContent,
            type: "bot",
            citations: data.citations || [],
            askForFeedback: data.askForFeedback,
            messageId: data.messageId,
            sessionId: data.sessionId,
            linkButtonItems: data.teneoWebClient?.linkbutton_items ?? [],
            quickReplies: data.teneoWebClient?.quick_replies,
            disclaimerText: data.disclaimerText ? data.disclaimerText : undefined,
            handoverSummary: data.handoverSummary,
            handover: data.handover ? data.handover : undefined,
          } as ChatMessage,
        ];
        localStorage.setItem(CHAT_HISTORY_BEFORE_LOGIN_KEY, JSON.stringify(messagesBeforeLogin));
        
        if (isOpen) {
          localStorage.setItem(CHAT_OPEN_BEFORE_LOGIN_KEY, JSON.stringify(true));
          handleMinimizeChat();
        }
        
        await startLogin();
        return;
      }

      const hasQuickReplies = data.teneoWebClient?.quick_replies && data.teneoWebClient.quick_replies.length > 0;
      const defaultContent = hasQuickReplies ? "" : "Jag har tyvärr inget svar just nu.";

      setMessages((prev) => [
        ...(prev || []),
        {
          sender: "Bot",
          content: data.content || defaultContent,
          type: "bot",
          citations: data.citations || [],
          askForFeedback: data.askForFeedback,
          messageId: data.messageId,
          sessionId: data.sessionId,
          linkButtonItems: data.teneoWebClient?.linkbutton_items ?? [],
          quickReplies: data.teneoWebClient?.quick_replies,
          disclaimerText: data.disclaimerText ? data.disclaimerText : undefined,
          agent: data.agent ? data.agent : undefined,
          handoverSummary: data.handoverSummary,
          handover: data.handover ? data.handover : undefined,
        },
      ]);
    } catch (err) {
      console.error("Failed to fetch chat response:", err, { requestBody });
      setMessages((prev) => [
        ...(prev || []),
        {
          sender: "Bot",
          content: "Tyvärr blev det ett tekniskt fel med chatten.",
          type: "bot",
          citations: [],
          askForFeedback: false,
          messageId: undefined,
          sessionId: undefined,
          linkButtonItems: [],
          quickReplies: undefined,
          agent: undefined,
          handoverSummary: undefined,
          handover: undefined,
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleLoginStateChange = async (loginState: {
    priorLoginState: string;
    newLoginState: string;
  }) => {
    if (loginState.newLoginState === "loggedOut") {
      handleCloseChat();
      return;
    }

    const lockTimestamp = localStorage.getItem(CHAT_LOGIN_HANDLING_KEY);
    const now = Date.now();

    if (lockTimestamp) {
      const lockAge = now - parseInt(lockTimestamp, 10);
      if (lockAge < LOGIN_DEBOUNCE_MS) {
        console.log('[ChatWidget] handleLoginStateChange - another call is handling login, skipping');
        return;
      }
    }
    localStorage.setItem(CHAT_LOGIN_HANDLING_KEY, now.toString());

    try {
      const wasOpenBeforeLogin = localStorage.getItem(CHAT_OPEN_BEFORE_LOGIN_KEY) === 'true';
      if (wasOpenBeforeLogin) {
        localStorage.removeItem(CHAT_OPEN_BEFORE_LOGIN_KEY);
        handleOpenChat();
      }

      const tokenData = await getTokenData();
      const userIsLoggedIn = tokenData?.isAnonymous === false;

      if (!userIsLoggedIn) return;

      const savedMessagesJson = localStorage.getItem(CHAT_HISTORY_BEFORE_LOGIN_KEY);

      if (savedMessagesJson) {
        try {
          const savedMessages = JSON.parse(savedMessagesJson) as ChatMessage[];

          localStorage.removeItem(CHAT_HISTORY_BEFORE_LOGIN_KEY);

          sendMessage(
            "user",
            "Nu ska jag ha loggat in. Välkomna mig gärna tillbaka till konversationen.",
            { autoPost: true, baseMessages: savedMessages },
          );

          return;
        } catch (error) {
          console.error('[ChatWidget] handleLoginStateChange - failed to restore messages:', error);
          localStorage.removeItem(CHAT_HISTORY_BEFORE_LOGIN_KEY);
        }
      }

      console.log('[ChatWidget] handleLoginStateChange - sending login notification');
      sendMessage(
        "user",
        "Nu ska jag ha loggat in. Välkomna mig gärna tillbaka till konversationen.",
        { autoPost: true },
      );
    } catch (error) {
      console.error('[ChatWidget] handleLoginStateChange - error:', error);
    } finally {
      setTimeout(() => {
        localStorage.removeItem(CHAT_LOGIN_HANDLING_KEY);
      }, LOGIN_DEBOUNCE_MS);
    }
  };

  const addMessageToHistory = (msg: ChatMessage) => {
    setMessages((prev) => [...prev, msg]);
  };

  const getLatestHandoverSummary = (messages: ChatMessage[]): string => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.type === "bot" && msg.handoverSummary) {
        return msg.handoverSummary;
      }
    }
    return "";
  }

  return (
    <div className={styles.container}>
      {mergedConfig.livechatProvider === "mitel" && (
        <MitelLivechat
          addMessageToHistory={addMessageToHistory}
          sendMessageToBotBackend={sendMessage}
          conversationSummary={getLatestHandoverSummary(messages)}
          onAgentTyping={setIsTyping}
        />
      )}
      {mergedConfig.livechatProvider === "giosg" && (
        <GiosgLivechat
          isLivechatAvailable={isLivechatAvailable!}
          messages={messages}
          onMinimize={handleMinimizeChat}
          addMessageToHistory={addMessageToHistory}
          sendMessageToBotBackend={sendMessage}
        />
      )}

      <ChatWidgetCommandHandler
        onOpenChat={handleOpenChat}
        onSetContext={handleSetContextCommand}
        onLoginChangeState={handleLoginStateChange}
        onMinimizeChat={handleMinimizeChat}
        onCloseChat={handleCloseChat}
        onStartLivechat={handleStartLivechat}
        onSendMessage={(message, options) => sendMessage("user", message, options)}
      />

      {!isOpen && showBubble && (
        <ChatBubble
          onClick={() => handleOpenChat()}
          imageURL={mergedConfig.launchIconImageURL}
          calloutMessage={mergedConfig.calloutMessage}
          enabledCallout={mergedConfig.enabledCallout}
        />
      )}
      {isOpen && !showCloseChatModal && (
        <ChatWindow
          config={mergedConfig}
          messages={messages}
          onMinimize={handleMinimizeChat}
          onClose={() => {
            if (mergedConfig.shouldShowCloseModal) {
              setShowCloseChatModal(true);
            } else {
              handleCloseChat();
            }
          }}
          onSendMessage={handleSendMessage}
          isTyping={isTyping}
        />
      )}      
      {isOpen && showCloseChatModal && mergedConfig.shouldShowCloseModal && (
        <CloseChatModal
          onCancelCloseChat={() => setShowCloseChatModal(false)}
          onConfirmCloseChat={() => {
            setShowCloseChatModal(false);
            handleCloseChat();
            setIsOpen(false);
          }}
          config={mergedConfig}
        />
      )}
    </div>
  );
};

export default ChatWidget;
